/**
 * ============================================================
 *  A/B Test Registry — single source of truth
 * ============================================================
 *
 *  To add a new test:
 *  1. Add a name constant:  export const AB_NEW_TEST = 'ab_new_test';
 *  2. Add a class constant: AB_NEW_TEST: 'ab-new-test-active'  (in CLASSES)
 *  3. Add an entry to TESTS array
 *  4. Add CSS: .ab-new-test-active .your-element { display: block; }
 *
 *  URL override: ?ab_hero=1 forces cookie value (useful for QA)
 */

// ─── Test name constants ────────────────────────────────────
// Import these wherever you need to reference a test by name
export const AB_HERO = 'ab_hero';
export const AB_SESSIONS = 'ab_sessions';
export const AB_SPORT_LOCATION = 'ab_sport_location';
export const AB_LISTING_MOBILE = 'ab_listing_mobile';
export const AB_CART = 'ab_cart';
export const AB_SESSION_DAYS_FORMAT = 'ab_session_days_format';
export const AB_SIDEBAR = 'ab_sidebar';
export const AB_SESSION_SEARCH_CARD = 'ab_session_search_card';
export const AB_CAMP_DISTANCE = 'ab_camp_distance';
export const AB_SESSION_SCHEDULE = 'ab_session_schedule';
export const AB_FEATURED = 'ab_featured';
export const AB_REMOVE_DISTANCE = 'ab_remove_distance';
export const AB_HERO_HOME = 'ab_hero_home';

// ─── CSS classes applied to <html> ──────────────────────────
export const CLASSES = {
  [AB_HERO]: 'ab-hero-active',
  [AB_SESSIONS]: 'ab-sessions-v3',
  [AB_SPORT_LOCATION]: 'ab-sport-location-v2',
  [AB_LISTING_MOBILE]: 'ab-listing-mobile-v2',
  [AB_CART]: 'ab-cart-v2',
  [AB_SESSION_DAYS_FORMAT]: 'ab-session-days-format-v2',
  [AB_SIDEBAR]: 'ab-sidebar-v2',
  [AB_SESSION_SEARCH_CARD]: 'ab-session-search-card-v2',
  [AB_CAMP_DISTANCE]: 'ab-camp-distance-v2',
  [AB_SESSION_SCHEDULE]: 'ab-session-schedule-v1',
  [AB_FEATURED]: 'ab-featured-v1',
  [AB_REMOVE_DISTANCE]: 'ab-remove-distance-v2',
  [AB_HERO_HOME]: 'ab-hero-home-v1',
};

// ─── Default config ─────────────────────────────────────────
export const DEFAULT_VALUES = ['0', '1'];
export const DEFAULT_DAYS = 30;
export const ACTIVE_VALUE = '1';

// ─── Sessions-specific values (3 variants: default, V3, V4) ─
export const SESSIONS_VALUES = ['0', '1', '2'];

// ─── Test registry ──────────────────────────────────────────
export const TESTS = [
  // Retired: V0 is the new default.
  // {
  //   name: AB_CART,
  //   values: DEFAULT_VALUES,
  //   days: DEFAULT_DAYS,
  //   activeOn: ACTIVE_VALUE,
  //   className: CLASSES[AB_CART],
  // },
  // {
  //   name: AB_SPORT_LOCATION,
  //   values: DEFAULT_VALUES,
  //   days: DEFAULT_DAYS,
  //   activeOn: ACTIVE_VALUE,
  //   className: CLASSES[AB_SPORT_LOCATION],
  // },
  // {
  //   name: AB_LISTING_MOBILE,
  //   values: DEFAULT_VALUES,
  //   days: DEFAULT_DAYS,
  //   activeOn: ACTIVE_VALUE,
  //   className: CLASSES[AB_LISTING_MOBILE],
  // },
  // Retired: V0 is the new default.
  // {
  //   name: AB_SIDEBAR,
  //   values: DEFAULT_VALUES,
  //   days: DEFAULT_DAYS,
  //   activeOn: ACTIVE_VALUE,
  //   className: CLASSES[AB_SIDEBAR],
  // },
  // Retired: V3 card is now the universal default (shouldRenderCampCardSessionSearchV3
  // always returns true). No cookie split, no variant class, no per-page registration.
  // {
  //   name: AB_SESSION_SEARCH_CARD,
  //   values: DEFAULT_VALUES,
  //   days: DEFAULT_DAYS,
  //   activeOn: ACTIVE_VALUE,
  //   className: CLASSES[AB_SESSION_SEARCH_CARD],
  // },
  // Retired 2026-06-15 — fully stopped, no longer needed.
  // {
  //   name: AB_CAMP_DISTANCE,
  //   values: DEFAULT_VALUES,
  //   days: DEFAULT_DAYS,
  //   activeOn: ACTIVE_VALUE,
  //   className: CLASSES[AB_CAMP_DISTANCE],
  //},
  // Session schedule: V0 = control card (no schedule), V1 = daily hours on the
  // session card + cart. DISABLED 2026-06-29 — registerAbTest commented in _camp.html
  // (no cookie/class) + cookie purged in runner.js. Const + CLASS kept dormant.
  // Re-enable = uncomment this entry + the _camp.html registration block.
  // {
  //   name: AB_SESSION_SCHEDULE,
  //   values: DEFAULT_VALUES,
  //   days: DEFAULT_DAYS,
  //   activeOn: ACTIVE_VALUE,
  //   className: CLASSES[AB_SESSION_SCHEDULE],
  // },
  // Featured camps: badge + pin-to-top in search/listings + Featured Camps
  // section on enabled sport pages. DISABLED 2026-06-29 — registerAbTest commented
  // in _layout.html (no cookie/class) + cookie purged in runner.js. Const + CLASS
  // kept as dormant "база". Re-enable = uncomment this entry + registerAbTest.
  // {
  //   name: AB_FEATURED,
  //   values: DEFAULT_VALUES,
  //   days: DEFAULT_DAYS,
  //   activeOn: ACTIVE_VALUE,
  //   className: CLASSES[AB_FEATURED],
  // },
  // Remove-distance: V0 = control (current — "X.X mi away" shown on every camp card),
  // V1 (activeOn) = variant that hides distance on ALL camp cards site-wide via the
  // ab-remove-distance-v2 <html> class (search results, listings, and the camps-near-you
  // blocks on the internal camp page). Registered globally in _layout.html.
  {
    name: AB_REMOVE_DISTANCE,
    values: DEFAULT_VALUES,
    days: DEFAULT_DAYS,
    activeOn: ACTIVE_VALUE,
    className: CLASSES[AB_REMOVE_DISTANCE],
  },
  // Homepage hero carousel — RETIRED 2026-07-02: variant won, carousel promoted to the
  // default homepage hero (index.html renders it directly, no registerAbTest). Entry
  // commented so the runner no longer sets the cookie/class; ab_hero_home added to the
  // cookie-cleanup list in runner.js to purge leftover cookies.
  // {
  //   name: AB_HERO_HOME,
  //   values: DEFAULT_VALUES,
  //   days: DEFAULT_DAYS,
  //   activeOn: ACTIVE_VALUE,
  //   className: CLASSES[AB_HERO_HOME],
  // },
  // {
  //   name: AB_HERO,
  //   values: DEFAULT_VALUES,
  //   days: DEFAULT_DAYS,
  //   activeOn: ACTIVE_VALUE,
  //   className: CLASSES[AB_HERO],
  //   pageSelector: '.rd-search-hero_home',
  // },
  // {
  //   name: AB_SESSIONS,
  //   values: SESSIONS_VALUES,
  //   days: DEFAULT_DAYS,
  //   activeOn: ACTIVE_VALUE,
  //   className: CLASSES[AB_SESSIONS],
  //   pageSelector: '.ab-sessions-variant',
  // },
];
